//REACT AND REACT BASED LIBRARIES
import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
//COMPONENTS
import Table from "@components/Table";
import { Box, Container } from "@mui/material";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import BackgroundImage from "@components/BackgroundImageCst";
import CustomRowAction from "@components/Table/components/CustomRowAction";
//CUSTOM HOOKS AND UTILS
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL, uploadFile } from "@utils/utilsApi";
import { useFetchMock } from "src/mock_data/useMockData";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { ActionsType } from "src/interfaces/Common";
import ButtonBackPage from "@components/ButtonBackPage";
import { useFetchTable } from "@hooks/useFetchDataTable";
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import UseCustomConfigBasedOnTableOverall from "../hooks";
import { useFilters } from "@context/filters";
import { useAuth } from "@context/auth";

const LOTableOverall: React.FC<{ type: string }> = ({ type }) => {
  const { id, idCourse, idPath, idUser } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();

  const navigate = useNavigate();
  const {
    getTranslation,
    getConfigBasedOnType,
    getEndpointBasedOnType,
    getNavigationModuleBasedOnType,
    formatDataBasedOnType,
    getEndpointFatherDetailBasedOnType,
  } = UseCustomConfigBasedOnTableOverall(type);

  const { t } = useTranslation([`overall-page`]);
  const filterId = `${user?.id_user}_${getConfigBasedOnType()}.${type}`;
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { columns, dataTable, actionsTopToolbar, actionForRow } = useFetchTable(
    getEndpointBasedOnType,
    getConfigBasedOnType(),
    filters[filterId],
  );

  const { dataDetail } = useFetchDetail(
    getEndpointFatherDetailBasedOnType,
    idCourse ? idCourse : idPath ? idPath : "",
    idCourse
      ? PagesID["corporate.catalog.courses"]
      : PagesID["corporate.catalog.path"],
  );

  const { dataDetail: dataCorporate } = useFetchDetail(
    ServicesURL.getCorporate,
    (type === "enrolled-to" && id) || "",
    PagesID["corporate"],
  );

  const { dataDetail: dataUser } = useFetchDetail(
    ServicesURL.customer,
    (type === "enrolled-to" && idUser + `?corporateId=${id}`) || "",
    PagesID["corporate.customers"],
  );

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.key) {
        case "subscribed":
          return {
            ...action,
            getIcon: () => <RecentActorsIcon />,
            callBack: (row: any) => {
              navigate(
                `${row?.id || row?.activity_id}/${row?.category || row?.type}/subscribed`,
              );
            },
          };
        case "view-module":
          return {
            ...action,
            callBack: (row: any) =>
              navigate(getNavigationModuleBasedOnType(row)),
          };
        case "change-status":
          return {
            ...action,
            callBack: (row: any, index: any) => {
              if (row.category === "DA_SCORM") {
                isMultiscorm(row, index);
              } else {
                navigate(getChangeStatusPath(row, "change-status"));
              }
            },
          };
        default:
          return action;
      }
    });
  }, [actionForRow]);

  // La gerarchia da inviare è quella dell'attività selezionata, non quella della rotta di provenienza
  const getChangeStatusPath = (row: any, section: string) =>
    `${row.activity_id}/${row.id_learning_object}/${row.parent_activity_id ?? "null"}/${row.grandparent_activity_id ?? "null"}/${row.category}/${section}`;

  const { trigger: triggerIsMultiScorm } = useSWRMutation(
    ServicesURL.scorm + `/is-multiscorm`,
    (url: string, { arg }: { arg: any }) => {
      return fetcher<any>(
        `${url}?corporateId=${id}&studentId=${idUser}&activityId=${arg.activity_id}&learningObjectId=${arg.id_learning_object}&parentActivityId=${arg.parent_activity_id || ""}&grandparentActivityId=${arg.grandparent_activity_id || ""}`,
        MethodType.GET,
      );
    },
    { onError: () => null },
  );

  const isMultiscorm = async (row: any, index: any) => {
    let result = await triggerIsMultiScorm(row);
    if (result) {
      navigate(getChangeStatusPath(row, "multi-scorm"));
    } else {
      navigate(getChangeStatusPath(row, "change-status"));
    }
  };

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    let activitiesCategories = [
      "DA_XAPI",
      "DA_SCORM",
      "DA_PHYSICAL_CLASS",
      "DA_VIRTUAL_CLASS",
      "DA_EXTERNAL_RES",
      "DA_YOUTUBE",
      "DA_LINKEDIN",
      "DA_LTI",
      "DA_SURVEY",
      "DA_URL_RES",
    ];

    configRowNew = configRowNew.map((action: any) => {
      if (row?.original.type === "ACTIVITY" && action.key === "view-module") {
        return { ...action, disabled: true };
      } else if (
        row?.original.type !== "ACTIVITY" &&
        action.key === "change-status" &&
        !activitiesCategories.includes(row?.original.category)
      ) {
        return { ...action, disabled: true };
      } else if (action.key === "change-status") {
        let activitiesCategories = [
          "DA_XAPI",
          "DA_SCORM",
          "DA_EXTERNAL_RES",
          "DA_YOUTUBE",
          "DA_LINKEDIN",
          "DA_LTI",
          "DA_SURVEY",
          "DA_URL_RES",
        ];

        if (
          activitiesCategories.includes(`${row?.original.category}`) &&
          row.original?.status === "E"
        ) {
          return { ...action, disabled: true };
        } else {
          return { ...action, disabled: false };
        }
      } else {
        return action;
      }
    });

    configRowNew = configRowNew.filter(
      (action: any) =>
        !(row?.original.type === "ACTIVITY" && action.key === "view-module"),
    );

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const filtersException = (filter: any, filterForm: any) => {
    let activitiesCategories = [
      "DA_XAPI",
      "DA_SCORM",
      "DA_PHYSICAL_CLASS",
      "DA_VIRTUAL_CLASS",
      "DA_EXTERNAL_RES",
      "DA_YOUTUBE",
      "DA_LINKEDIN",
      "DA_SURVEY",
      "DA_URL_RES",
      "DA_MATERIAL",
      "DA_GOALS",
      "DA_MEETING",
      "DA_FINAL_BALANCE",
    ];
    let coursesPathsCategories = ["BLENDED", "ASYNC", "SYNC"];
    if (filter.accessorKey === "category") {
      if (
        filterForm?.type &&
        (filterForm?.type === "COURSE" || filterForm?.type === "PATH")
      ) {
        filter = { ...filter, optionsToDisable: activitiesCategories };
      }
      if (filterForm?.type && filterForm?.type === "ACTIVITY") {
        filter = { ...filter, optionsToDisable: coursesPathsCategories };
      }
    }

    if (filter.accessorKey === "type") {
      if (!filterForm?.category && type === "associated-activities") {
        filter = {
          ...filter,
          optionsToDisable: [
            ...(filter?.optionsToDisable || []),
            "COURSE",
            "PATH",
          ],
        };
      }
      if (!filterForm?.category && type === "associated-los") {
        filter = {
          ...filter,
          optionsToDisable: [...(filter?.optionsToDisable || []), "PATH"],
        };
      }
      if (
        filterForm?.category &&
        coursesPathsCategories.includes(filterForm?.category)
      ) {
        filter = {
          ...filter,
          optionsToDisable: [...(filter?.optionsToDisable || []), "ACTIVITY"],
        };
      }
      if (
        filterForm?.category &&
        activitiesCategories.includes(filterForm?.category)
      ) {
        filter = {
          ...filter,
          optionsToDisable: [
            ...(filter?.optionsToDisable || []),
            "COURSE",
            "PATH",
          ],
        };
      }
    }

    return filter;
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-certificates.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${
            type === "associated-activities" || type === "associated-los"
              ? `${dataDetail?.title} - `
              : type === "enrolled-to"
                ? `${dataCorporate?.name} - `
                : ""
          }${t(`${getTranslation()}.hero-title`)}
          ${
            type === "enrolled-to"
              ? `${dataUser?.name} ${dataUser?.surname}`
              : ""
          }
          `}
          description={t(`${getTranslation()}.hero-description`)}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={`${getConfigBasedOnType()}.${type}`}
            data={formatDataBasedOnType(
              dataTable?.output ||
                dataTable?.pathChildrenList ||
                dataTable?.courseChildrenList ||
                [],
            )}
            columns={columns || []}
            configurationTopToolbar={actionsTopToolbar}
            renderRowActions={customRenderRowActions}
            totalPages={dataTable?.metadata?.total_elements}
            filtersException={filtersException}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
      </Container>
    </AnimationFadeIn>
  );
};

export default LOTableOverall;
